import { API_BASE } from '../../config/apiBase';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Input } from '../../components/ui/input';
import { 
  Building2,
  Plus,
  ArrowRight,
  Search,
  CheckCircle
} from 'lucide-react';
import { cn } from '../../lib/utils';

const API = API_BASE;

const BankAnalysisList = () => {
  const navigate = useNavigate();
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchAnalyses();
  }, []);

  const fetchAnalyses = async () => {
    try {
      const response = await axios.get(`${API}/bank-analyses`);
      setAnalyses(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAnalyses = analyses.filter(a =>
    !search || (a.client_name?.toLowerCase().includes(search.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-48" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bank-analysis-list-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] flex items-center gap-2">
            <Building2 className="w-6 h-6 text-emerald-600" />
            Análisis Bancario
          </h1>
          <p className="text-muted-foreground">Análisis de estados de cuenta bancarios</p>
        </div>
        <Button onClick={() => navigate('/apps/bank-analysis/new')}>
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Análisis
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
        <Input
          placeholder="Buscar por cliente..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {filteredAnalyses.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAnalyses.map((analysis) => (
            <Card 
              key={analysis.id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(`/apps/bank-analysis/${analysis.id}`)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      'w-12 h-12 rounded-lg flex items-center justify-center',
                      analysis.status === 'completed' ? 'bg-emerald-100' : 'bg-muted'
                    )}>
                      <Building2 className={cn(
                        'w-6 h-6',
                        analysis.status === 'completed' ? 'text-emerald-600' : 'text-muted-foreground'
                      )} />
                    </div>
                    <div>
                      <CardTitle className="text-base">Análisis Bancario</CardTitle>
                      <CardDescription>{analysis.client_name || 'Sin cliente'}</CardDescription>
                    </div>
                  </div>
                  <Badge variant={analysis.status === 'completed' ? 'default' : 'secondary'}>
                    {analysis.status === 'completed' ? 'Completado' : 'Borrador'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-muted-foreground">Ingreso Prom.</p>
                    <p className="font-medium text-emerald-600">${analysis.avg_monthly_income?.toLocaleString() || 0}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Balance Mín.</p>
                    <p className="font-medium">${analysis.min_ending_balance?.toLocaleString() || 0}</p>
                  </div>
                </div>
                <div className="flex items-center text-sm text-primary font-medium pt-2">
                  Ver detalles
                  <ArrowRight className="w-4 h-4 ml-1" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="py-12">
          <CardContent className="text-center">
            <Building2 className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-semibold mb-2">No hay análisis bancarios</h3>
            <p className="text-muted-foreground mb-4">Comienza creando tu primer análisis</p>
            <Button onClick={() => navigate('/apps/bank-analysis/new')}>
              <Plus className="w-4 h-4 mr-2" /> Nuevo Análisis
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BankAnalysisList;
